import { z } from "zod";
import {
  BASE_STATES,
  STATUS_MODULES,
  STATUS_TONES,
  TRANSITION_ACTIONS,
  actionsProblem,
  type BaseState,
  type EffectStep,
  type StatusModule,
  type StatusTone,
  type TransitionAction,
} from "@cx/domain";
import { ulidParam } from "./ids";

// ===== Situações e Gerenciador de transições (RF-PRF-03) =====

export const statusModule = z.enum(STATUS_MODULES, "Módulo inválido");
export const statusModuleQuery = z.object({ module: statusModule });
export type StatusModuleQuery = z.infer<typeof statusModuleQuery>;

/** Edição: rótulo, cor e posição; o estado base só muda em situação customizada (o DO recusa na de sistema). */
export const statusInput = z.object({
  label: z.string().trim().min(1, "Informe o nome").max(40, "Até 40 caracteres"),
  color: z.enum(STATUS_TONES, "Cor inválida").default("secondary"),
  baseState: z.enum(BASE_STATES, "Estado base inválido"),
  position: z
    .number()
    .int("Posição inválida")
    .min(0, "Posição inválida")
    .max(999, "Posição inválida")
    .default(0),
});
export type StatusInput = z.output<typeof statusInput>;
export type StatusFormValues = z.input<typeof statusInput>;

export const statusCreateInput = statusInput.extend({ module: statusModule });
export type StatusCreateInput = z.output<typeof statusCreateInput>;

export type StatusView = {
  id: string;
  module: StatusModule;
  code: string;
  label: string;
  color: StatusTone;
  baseState: BaseState;
  isSystem: boolean;
  position: number;
};

const actions = z
  .array(z.enum(TRANSITION_ACTIONS, "Ação inválida"), "Informe as ações")
  .max(TRANSITION_ACTIONS.length, "Ações demais")
  .superRefine((list, ctx) => {
    // O módulo e o destino (situação cancelada) são conferidos no DO, que conhece as situações.
    const p = actionsProblem(null, list);
    if (p) ctx.addIssue({ code: "custom", message: p.message });
  });

export const transitionInput = z.object({ actions: actions.default([]) });
export type TransitionInput = z.output<typeof transitionInput>;

export const transitionCreateInput = transitionInput
  .extend({ fromStatusId: ulidParam, toStatusId: ulidParam })
  .refine((v) => v.fromStatusId !== v.toStatusId, {
    path: ["toStatusId"],
    message: "A situação de destino deve ser diferente da de origem",
  });
export type TransitionCreateInput = z.output<typeof transitionCreateInput>;

export type TransitionView = {
  id: string;
  module: StatusModule;
  fromStatusId: string;
  toStatusId: string;
  actions: TransitionAction[];
};

export type StatusBoard = { statuses: StatusView[]; transitions: TransitionView[] };

/**
 * Mudança de situação de um documento (RPC do DO; a rota HTTP vem com cada documento, ex.: `/sales-orders/:id/status`).
 * `expectedFromStatusId` (obrigatório): a situação que a tela mostrava; se outro usuário mudou antes, 409
 * `status_changed` (já no destino não é conflito: clique repetido).
 */
export const documentTransitionInput = z.object({
  toStatusId: ulidParam,
  expectedFromStatusId: ulidParam,
});
export type DocumentTransitionInput = z.output<typeof documentTransitionInput>;

export type DocumentTransitionResult = {
  changed: boolean;
  fromStatusId: string;
  toStatusId: string;
  steps: EffectStep[];
};
